import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import moment from 'moment';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';

@Injectable({
  providedIn: 'root'
})
export class UnpaidOrderService {
  protected basePath = '/';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration
  ) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  getUnpaidOrders(page: number = 1, pageSize: number = 10, startDate?: string, endDate?: string, searchText?: string, userId?: string): Observable<any> {
    let params = new HttpParams()
      .set('Page', page.toString())
      .set('PageSize', pageSize.toString());

    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    if (userId) {
      params = params.set('userId', userId);
    }
    if (searchText && searchText.trim()) {
      params = params.set('Filters', `(extend_product_name|code|phone|extend_company_tax_code|fullname)@=*${searchText.trim()}`);
    }

    return this.httpClient.get<any>(`${this.basePath}/api/debt/unpaidOrders`, {
      params,
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  getDebtSummary(startDate?: string, endDate?: string): Observable<any> {
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.httpClient.get<any>(`${this.basePath}/api/debt/summary`, {
      params,
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  getDebtOrders(page: number = 1, pageSize: number = 10, startDate?: string, endDate?: string, searchText?: string): Observable<any> {
    let params = new HttpParams()
      .set('Page', page.toString())
      .set('PageSize', pageSize.toString());

    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    if (searchText && searchText.trim()) {
      params = params.set('Filters', `(customerName|orderCode|customerPhone)@=*${searchText.trim()}`);
    }

    return this.httpClient.get<any>(`${this.basePath}/api/debt/orders`, {
      params,
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  getUnpaidUserOrders(page: number = 1, pageSize: number = 10, startDate?: string, endDate?: string, searchText?: string): Observable<any> {
    let params = new HttpParams()
      .set('Page', page.toString())
      .set('PageSize', pageSize.toString());

    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    if (searchText && searchText.trim()) {
      params = params.set('Filters', `(extend_product_name|code|phone|extend_company_tax_code|fullname)@=*${searchText.trim()}`);
    }

    return this.httpClient.get<any>(`${this.basePath}/api/debt/unpaidUserOrders`, {
      params,
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  getOrderPaymentsByOrder(orderId: string, page: number = 1, pageSize: number = 10): Observable<any> {
    let params = new HttpParams()
      .set('Page', page.toString())
      .set('PageSize', pageSize.toString());
    return this.httpClient.get<any>(`${this.basePath}/api/orders/${encodeURIComponent(String(orderId))}/orderPayments`, {
      params,
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  createOrderPayment(payload: { order_id: string, payment_no: number, amount: number, payment_method: string, bank_name: string, payment_date: string, note: string }): Observable<any> {
    return this.httpClient.post<any>(`${this.basePath}/api/orderPayments`, payload, {
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  updateOrderPayment(id: string, payload: { order_id: string, payment_no: number, amount: number, payment_method: string, bank_name: string, payment_date: string, note: string }): Observable<any> {
    return this.httpClient.put<any>(`${this.basePath}/api/orderPayments/${encodeURIComponent(String(id))}`, payload, {
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }
  
  getOrderPayment(id: string): Observable<any> {
    return this.httpClient.get<any>(`${this.basePath}/api/orderPayments/${encodeURIComponent(String(id))}`, {
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  getOrderDebtConfig(orderId: string): Observable<any> {
    return this.httpClient.get<any>(`${this.basePath}/api/debt/order-config/${encodeURIComponent(String(orderId))}`, {
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  setOrderDebtConfig(payload: { orderId: string, dueDate?: string | null, debtDays?: number | null, reason?: string }): Observable<any> {
    return this.httpClient.post<any>(`${this.basePath}/api/debt/orderConfig`, payload, {
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  deleteOrderDebtConfig(orderId: string): Observable<any> {
    return this.httpClient.delete<any>(`${this.basePath}/api/debt/orderConfig/${encodeURIComponent(String(orderId))}`, {
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }

  deleteOrderPayment(id: string): Observable<any> {
    return this.httpClient.delete<any>(`${this.basePath}/api/orderPayments/${encodeURIComponent(String(id))}`, {
      withCredentials: this.configuration.withCredentials,
      headers: this.defaultHeaders
    });
  }
}
